'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { submitSponsorshipInquiry, SponsorshipInquiryError } from '@/lib/sponsorship';

export default function SponsorshipInquiryForm() {
  const router = useRouter();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    tier: '',
    message: '',
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('active');
        });
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await submitSponsorshipInquiry(form);
      router.push(`/sponsorship/thank-you?name=${encodeURIComponent(form.name)}`);
    } catch (err) {
      setError(
        err instanceof SponsorshipInquiryError
          ? err.message
          : 'Could not send your inquiry. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="inquiry"
      ref={sectionRef}
      className="bg-dark-bg noise-overlay w-full px-4 md:px-12 pt-14 md:pt-20 pb-16 md:pb-20"
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8 md:mb-12 reveal">
          <span className="inline-block px-4 py-1.5 rounded-full border border-accent/30 text-accent text-xs font-bold tracking-widest uppercase mb-4 md:mb-5">
            Get In Touch
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight mb-3 md:mb-4">
            Let&apos;s Write This Chapter <span className="text-accent">Together</span>
          </h2>
          <p className="text-white/60 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
            Interested in partnering with Prathaa 2026? Send us an inquiry and we&apos;ll share the
            full sponsorship deck.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="reveal bg-dark-card border border-accent/15 rounded-3xl md:rounded-4xl p-6 md:p-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mb-4 md:mb-5">
            <div>
              <label className="block text-white/60 text-xs font-bold tracking-widest uppercase mb-2">
                Your Name *
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                placeholder="Vikram Mehta"
                className="form-input bg-dark-bg/60 border-white/10 text-white placeholder:text-white/30 focus:border-accent"
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs font-bold tracking-widest uppercase mb-2">
                Company / Brand *
              </label>
              <input
                type="text"
                name="company"
                value={form.company}
                onChange={handleChange}
                required
                placeholder="Brand India Pvt. Ltd."
                className="form-input bg-dark-bg/60 border-white/10 text-white placeholder:text-white/30 focus:border-accent"
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs font-bold tracking-widest uppercase mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                placeholder="vikram@brandindia.com"
                className="form-input bg-dark-bg/60 border-white/10 text-white placeholder:text-white/30 focus:border-accent"
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs font-bold tracking-widest uppercase mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="+91 98200 00000"
                className="form-input bg-dark-bg/60 border-white/10 text-white placeholder:text-white/30 focus:border-accent"
              />
            </div>
          </div>
          <div className="mb-4 md:mb-5">
            <label className="block text-white/60 text-xs font-bold tracking-widest uppercase mb-2">
              Sponsorship Tier Interest *
            </label>
            <select
              name="tier"
              value={form.tier}
              onChange={handleChange}
              required
              className="form-input bg-dark-bg/60 border-white/10 text-white focus:border-accent"
            >
              <option value="" className="bg-dark-bg text-white">
                Select a tier...
              </option>
              <option value="platinum" className="bg-dark-bg text-white">
                Platinum — ₹15 Lakh (Title Sponsor)
              </option>
              <option value="gold" className="bg-dark-bg text-white">
                Gold — ₹10 Lakh (Category Sponsor)
              </option>
              <option value="silver" className="bg-dark-bg text-white">
                Silver — ₹5 Lakh (Supporting Sponsor)
              </option>
              <option value="custom" className="bg-dark-bg text-white">
                Custom Package
              </option>
            </select>
          </div>
          <div className="mb-5 md:mb-7">
            <label className="block text-white/60 text-xs font-bold tracking-widest uppercase mb-2">
              Message
            </label>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              rows={4}
              placeholder="Tell us about your brand and what you're looking for from this partnership..."
              className="form-input bg-dark-bg/60 border-white/10 text-white placeholder:text-white/30 focus:border-accent resize-none"
            />
          </div>
          {error && (
            <div className="mb-4 md:mb-5 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-sm text-red-400">
              {error}
            </div>
          )}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground pl-7 md:pl-8 pr-3 py-3.5 md:py-4 rounded-full font-bold text-sm md:text-base tracking-wide hover:bg-secondary transition-all duration-300 shadow-crimson group disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Sending…' : 'Send Inquiry'}
              <span className="bg-primary-foreground/15 p-2 rounded-full group-hover:translate-x-0.5 transition-transform">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </span>
            </button>
            <a
              href="/assets/images/HCAF_PRATHA_2026_Sponsorship_Deck-71.pdf"
              download
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 text-accent font-semibold text-sm hover:text-secondary transition-colors duration-200"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" x2="12" y1="15" y2="3" />
              </svg>
              Download Sponsorship Deck (PDF)
            </a>
          </div>
        </form>
      </div>
    </section>
  );
}
